import { InvoiceStatus } from '@iota/iota-management-portal-v4-client';
import { AuditInvoiceSummaryEntity } from 'src/app/store/entity-models';
import { isoDateDaysAgo } from 'src/app/shared/utils/date.util';

export function billingDefaultDateFrom(): string {
    return isoDateDaysAgo(20);
}

export const InvoiceStatusToThemeColor: Partial<Record<InvoiceStatus, string>> = {
    [InvoiceStatus.TO_BE_REVIEWED]: 'bg-iota-requested text-yellow-700',
    [InvoiceStatus.REVIEWED_AND_APPROVED]: 'bg-iota-inh-green text-white',
    [InvoiceStatus.REVIEWED_AND_REJECTED]: 'bg-iota-rejected text-red-700',
    [InvoiceStatus.GENERATION_FAILED]: 'bg-iota-rejected text-red-700'
};
export const InvoiceStatusLabels: Partial<Record<InvoiceStatus, string>> = {
    [InvoiceStatus.TO_BE_REVIEWED]: $localize`:@@newSingular:New`,
    [InvoiceStatus.REVIEWED_AND_APPROVED]: $localize`Approved`,
    [InvoiceStatus.REVIEWED_AND_REJECTED]: $localize`Rejected`,
    [InvoiceStatus.GENERATION_FAILED]: $localize`Failed`
};

export enum AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER {
    ALL = 'ALL',
    FAILED = 'FAILED',
    REJECTED = 'REJECTED',
    APPROVED = 'APPROVED'
}
export const AuditInvoiceSummaryFilterLabels: { [K in AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER]: string } = {
    [AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.ALL]: $localize`All selected`,
    [AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.APPROVED]: $localize`Approved`,
    [AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.REJECTED]: $localize`Rejected`,
    [AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.FAILED]: $localize`Failed`
};

export type AuditInvoiceSummaryProperties = Partial<Record<keyof AuditInvoiceSummaryEntity, string>>;

export const AuditInvoiceSummaryKeyLabels: Required<AuditInvoiceSummaryProperties> = {
    auditInvoiceSummaryId: $localize`Audit Invoice Summary ID`,
    executionTime: $localize`Document date`,
    executorUsername: $localize`Executor Username`,
    executionJobId: $localize`Execution Job ID`,
    centralRegulatorId: $localize`Central Regulator ID`,
    companyId: $localize`Company ID`,
    invoiceDispatchDatetime: $localize`Invoice dispatch date`,
    invoiceDispatchedBy: $localize`Invoice dispatched by`,
    documentId: $localize`Document ID`,
    invoiceNumber: $localize`Document no.`,
    totalAmountInvoicedNet: $localize`Amount (net)`,
    totalAmountInvoicedGross: $localize`Amount`,
    summedTaxInformation: $localize`Tax information`,
    auditInvoiceGenerations: $localize`Invoice items`,
    invoiceStatus: $localize`Status`,
    invoiceReviewNotes: $localize`Notes`,
    invoiceReviewAttachment: $localize`Attachment`,
    creationBy: $localize`Created by`,
    creationById: $localize`Created by ID`,
    creationDateTime: $localize`Creation date`,
    modificationBy: $localize`Modification by`,
    modificationById: $localize`Modification by ID`,
    modificationDateTime: $localize`Modification date`,
    validFrom: $localize`Valid from`,
    validTo: $localize`Valid to`,
    skId: $localize`SK-ID`,
    errorMessage: $localize`Error`,
    fullErrorLog: $localize`Error log`,
    invoiceType: $localize`Invoice type`,
    replacedByAuditInvoiceSummaryId: $localize`Replaced by`,

    businessPartnerDetailsUrl: $localize`Business partner URL`,
    businessPartnerName: $localize`Business partner`,
    businessPartnerNumber: $localize`Business partner no.`,
    documentUnread: '',
    hasAtLeastOneNote: '',
    canBeRejectedAndApproved: '',
    hasFailedStatus: '',

    firstColumnPlaceholder: ''
};

export const DEFAULT_AUDIT_INVOICE_SUMMARY_OVERVIEW_TABLE_HEADERS: (keyof AuditInvoiceSummaryProperties)[] = [
    'firstColumnPlaceholder',
    'executionTime',
    'invoiceNumber',
    'totalAmountInvoicedGross',
    'businessPartnerNumber',
    'businessPartnerName',
    'invoiceStatus'
];

export const AuditInvoiceSummaryOverviewTableHeaders: {
    [K in AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER]: (keyof AuditInvoiceSummaryProperties)[];
} = {
    [AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.ALL]: DEFAULT_AUDIT_INVOICE_SUMMARY_OVERVIEW_TABLE_HEADERS,
    [AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.APPROVED]: DEFAULT_AUDIT_INVOICE_SUMMARY_OVERVIEW_TABLE_HEADERS,
    [AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.REJECTED]: DEFAULT_AUDIT_INVOICE_SUMMARY_OVERVIEW_TABLE_HEADERS,
    [AUDIT_INVOICE_SUMMARY_OVERVIEW_FILTER.FAILED]: [
        'firstColumnPlaceholder',
        'executionTime',
        'businessPartnerNumber',
        'businessPartnerName',
        'errorMessage'
    ]
};

export const AUDIT_INVOICE_SUMMARY_IDS: (keyof AuditInvoiceSummaryProperties)[] = [
    'auditInvoiceSummaryId',
    'companyId',
    'documentId',
    'executionJobId'
];

export const AUDIT_INVOICE_SUMMARY_DATE_PROPERTIES: (keyof AuditInvoiceSummaryProperties)[] = [
    'creationDateTime',
    'modificationDateTime',
    'invoiceDispatchDatetime',
    'executionTime'
];
export const AUDIT_INVOICE_SUMMARY_PROPERTIES_WITH_CURRENCY: (keyof AuditInvoiceSummaryProperties)[] = [
    'totalAmountInvoicedGross',
    'totalAmountInvoicedNet'
];

export const AuditInvoiceSummaryKeySuffixes: AuditInvoiceSummaryProperties = {
    totalAmountInvoicedGross: ' €',
    totalAmountInvoicedNet: ' €'
};
